import { ChangeDetectionStrategy, Component, computed, Inject, inject, OnInit, signal, Signal } from '@angular/core';
import { MatDialogConfig, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import EstensaModel from 'estensa-module';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import {
    AllCommunityModule,
    ColDef,
    GridApi,
    GridReadyEvent,
    GridSizeChangedEvent,
    SizeColumnsToFitGridStrategy,
} from 'ag-grid-community';
import { AgGridModule } from 'ag-grid-angular';
import { BrokerTableDataDisplay } from '../../broker.models';
import { themeEstensa } from '@se/se.platform.base';

@Component({
    selector: 'app-broker-confirm-dialog',
    templateUrl: './broker-confirm-dialog.component.html',
    styleUrls: ['./broker-confirm-dialog.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [MatDialogConfig],
    imports: [MatButtonModule, MatDialogModule, TranslateModule, AgGridModule],
})
export class BrokerConfirmDialogComponent implements OnInit {
    translateService = inject(TranslateService);
    title = signal<string>(this.data.title);
    message = signal<string>(this.data.message);
    hasYes = signal<boolean>(this.data.hasYes);
    hasNo = signal<boolean>(this.data.hasNo);
    hasOk = signal<boolean>(this.data.hasOk);
    hasCancel = signal<boolean>(this.data.hasCancel);

    dialogRef = inject(MatDialogRef<BrokerConfirmDialogComponent>);

    //ag-grid
    gridApi?: GridApi;
    agGridModuls = [AllCommunityModule];
    agGridTheme = themeEstensa;

    autoSizeStrategy: SizeColumnsToFitGridStrategy = {
        type: 'fitGridWidth',
    };

    dataSource = signal<BrokerTableDataDisplay[]>([]);
    noRowsTemplate: string = this.translateService.instant('se-ui-datagrid_no_row_overlay');

    columnDefs: ColDef[] = [
        {
            field: 'cognome',
            headerName: this.translateService.instant('LastName'),
            headerTooltip: this.translateService.instant('LastName'),
            tooltipField: 'cognome',
            suppressSizeToFit: true,
            sortable: true,
            //flex: 1,
            width: 150,
            resizable: false,
            sort: 'desc',
        },
        {
            field: 'nome',
            headerName: this.translateService.instant('FirstName'),
            headerTooltip: this.translateService.instant('FirstName'),
            tooltipField: 'nome',
            suppressSizeToFit: true,
            sortable: true,
            //flex: 1,
            width: 150,
            resizable: false,
        },
        {
            field: 'sesso',
            headerName: this.translateService.instant('Sex'),
            headerTooltip: this.translateService.instant('Sex'),
            tooltipField: 'sesso',
            suppressSizeToFit: true,
            sortable: true,
            //flex: 1,
            width: 100,
            resizable: false,
        },
        {
            field: 'patientBirthDateFormatted',
            headerName: this.translateService.instant('BirthDate'),
            headerTooltip: this.translateService.instant('BirthDate'),
            tooltipField: 'patientBirthDateFormatted',
            suppressSizeToFit: true,
            sortable: true,
            //flex: 1,
            width: 200,
            resizable: false,
        },
        {
            field: 'description',
            headerName: this.translateService.instant('Description'),
            headerTooltip: this.translateService.instant('Description'),
            tooltipField: 'description',
            suppressSizeToFit: true,
            cellStyle: { 'white-space': 'normal', 'line-height': '1.5', wrapText: 'true', padding: '10px ' },
            autoHeight: true,
            sortable: true,
            flex: 1,
            resizable: false,
        },
    ];

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            origin: {
                suspectedStudies: EstensaModel.BrokerDataDisplay[];
                modifiedStudies: EstensaModel.BrokerDataDisplay[];
                diferentData: EstensaModel.BrokerDataDisplay[];
                pirata: EstensaModel.IBrokerDataDisplay[];
            };

            title: string;
            message: string;
            hasOk: boolean;
            hasCancel: boolean;
            hasYes: boolean;
            hasNo: boolean;
        },
    ) {
        if (data && data.origin && data.origin.pirata) {
            this.dataSource.set(this.initSignalValue(this.data));
        }
    }

    ngOnInit() {
    }

    private initSignalValue = (data: any): BrokerTableDataDisplay[] => {
        let array: EstensaModel.BrokerDataDisplay[] =
            data.origin.pirata?.length > 0
                ? data.origin.pirata
                : data.origin.diferentData?.length > 0
                  ? data.origin.diferentData
                  : data.origin.modifiedStudies?.length > 0
                    ? data.origin.modifiedStudies
                    : data.origin.suspectedStudies?.length > 0
                      ? data.origin.suspectedStudies
                      : [];
        return array.length
            ? array.map((el: EstensaModel.BrokerDataDisplay) => {
                  return {
                      cognome: el.patientInfo.cognome,
                      nome: el.patientInfo.nome,
                      sesso: el.patientInfo.sesso,
                      patientBirthDateFormatted: el.patientBirthDateFormatted,
                      description: el.description,
                  };
              })
            : [];
    };

    onGridReady(params: GridReadyEvent) {
        this.gridApi = params.api;
        this.resize();
    }

    onGridSizeChanged(_event: GridSizeChangedEvent) {
        this.resize();
    }

    private resize = () => {
        this.gridApi?.sizeColumnsToFit();
    };

    closeDialog(): void {
        this.dialogRef.close({ action: 'Cancel', data: this.data });
    }

    onOk(): void {
        this.dialogRef.close({ action: 'Ok', data: this.data });
    }

    onYes(): void {
        this.dialogRef.close({ action: 'Yes', data: this.data });
    }

    onNo(): void {
        this.dialogRef.close({ action: 'No', data: this.data });
    }
}
